import { Trans, useLingui } from "@lingui/react/macro";
import { useId, type ReactNode } from "react";

import { DEFAULT_LOCALE, isLocale, type Locale } from "@/shared/i18n";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/shared/ui/dialog";

import matchesEn from "../assets/how-it-works/matches.en.webp";
import matchesRu from "../assets/how-it-works/matches.ru.webp";
import progressEn from "../assets/how-it-works/progress.en.webp";
import progressRu from "../assets/how-it-works/progress.ru.webp";
import reportEn from "../assets/how-it-works/report.en.webp";
import reportRu from "../assets/how-it-works/report.ru.webp";

type Screenshots = { matches: string; progress: string; report: string };

// Скриншоты настоящих экранов на демо-данных, на каждом языке свои. Снимает `pnpm screenshots`
// (tests/screenshots/): поменялись эти экраны — переснять.
const SCREENSHOTS: Record<Locale, Screenshots> = {
  ru: { matches: matchesRu, progress: progressRu, report: reportRu },
  en: { matches: matchesEn, progress: progressEn, report: reportEn },
};

// Размер файлов скриншотов: кадр 720×450 с двойной плотностью.
const SCREENSHOT_WIDTH = 1440;
const SCREENSHOT_HEIGHT = 900;

const BROWSER_DOTS = [0, 1, 2];

type Step = {
  id: keyof Screenshots;
  // Что на скриншоте: подпись увеличенного скриншота и его alt.
  caption: string;
  title: ReactNode;
  text: ReactNode;
};

// «Как это работает» на странице входа (макет design/prototype/Login.dc.html): три шага от списка
// матчей до готового разбора. Скриншот — миниатюра в рамке окна, по нажатию открывается крупно.
export const HowItWorks = () => {
  const { i18n, t } = useLingui();
  const headingId = useId();
  const screenshots = SCREENSHOTS[isLocale(i18n.locale) ? i18n.locale : DEFAULT_LOCALE];

  const steps: Step[] = [
    {
      id: "matches",
      caption: t`Your recent matches with the Analyze button`,
      title: <Trans>Pick a match</Trans>,
      text: <Trans>After you sign in, your recent matches are here. Click “Analyze” on one.</Trans>,
    },
    {
      id: "progress",
      caption: t`The analysis is being prepared: its steps`,
      title: <Trans>Wait half a minute</Trans>,
      text: (
        <Trans>
          We compare you with strong players on the same hero. You see every step on the screen.
        </Trans>
      ),
    },
    {
      id: "report",
      caption: t`A finished analysis: the traffic light and mistakes`,
      title: <Trans>See what to fix</Trans>,
      text: (
        <Trans>
          A traffic light and the mistakes to start with: buying, deaths, enemies who got away.
        </Trans>
      ),
    },
  ];

  return (
    <>
      <h2 id={headingId} className="mt-2 font-display text-[22px]">
        <Trans>How it works</Trans>
      </h2>
      <ol
        aria-labelledby={headingId}
        className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4"
      >
        {steps.map(({ caption, ...step }, index) => (
          <li key={step.id} data-step={step.id} className="flex flex-col gap-2.5">
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  aria-label={t`Enlarge the screenshot: ${caption}`}
                  className="block w-full cursor-zoom-in overflow-hidden rounded-[10px] border border-border bg-surface transition-colors outline-none hover:border-border-strong focus-visible:ring-2 focus-visible:ring-accent/60"
                >
                  <span
                    aria-hidden="true"
                    className="flex gap-[5px] border-b border-border px-2.5 py-[7px]"
                  >
                    {BROWSER_DOTS.map((dot) => (
                      <span key={dot} className="size-1.5 rounded-full bg-border" />
                    ))}
                  </span>
                  <img
                    src={screenshots[step.id]}
                    alt=""
                    width={SCREENSHOT_WIDTH}
                    height={SCREENSHOT_HEIGHT}
                    decoding="async"
                    className="block aspect-[16/10] h-auto w-full"
                  />
                </button>
              </DialogTrigger>
              <DialogContent
                aria-describedby={undefined}
                className="gap-3 p-4 sm:max-w-[min(1100px,calc(100%-2rem))]"
              >
                <DialogTitle className="pr-8 font-sans text-sm leading-normal text-foreground-soft">
                  {caption}
                </DialogTitle>
                <img
                  src={screenshots[step.id]}
                  alt={caption}
                  width={SCREENSHOT_WIDTH}
                  height={SCREENSHOT_HEIGHT}
                  className="mx-auto block h-auto max-h-[calc(100dvh-8rem)] w-auto max-w-full rounded-lg border border-border"
                />
              </DialogContent>
            </Dialog>
            <div className="flex items-start gap-2.5">
              <span
                aria-hidden="true"
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-accent font-mono text-[13px] font-semibold text-accent-foreground"
              >
                {index + 1}
              </span>
              <div className="flex flex-col gap-0.5">
                <h3 className="text-[15px] font-semibold">{step.title}</h3>
                <p className="text-[13px] text-muted">{step.text}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </>
  );
};
